import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { resolveAssetUrl } from '../utils/api';
import ConfirmModal from './ConfirmModal';
import { ANGLE_ORDER, detectAngleFromUrl } from '../utils/imageAngles';

export default function ProductGalleryEditor({
  gallery,
  uploading,
  disabled = false,
  onAddFiles,
  onRemove,
  onMove,
  onSetCover,
  onAssignAngle
}) {
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState(null);

  const pendingImage =
    pendingRemoveIndex === null ? null : gallery[pendingRemoveIndex];

  return (
    <div className="space-y-4 rounded-2xl border border-slate-200 bg-slate-50/70 p-4 md:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <label className="text-sm font-bold uppercase tracking-wide text-slate-800">
            Product Angles
          </label>
          <p className="mt-1 text-sm text-slate-500">
            Front is the storefront thumbnail. Assign angles for cleaner gallery organization.
          </p>
        </div>
        <span className="badge badge-neutral">
          {gallery.length} image{gallery.length === 1 ? '' : 's'}
        </span>
      </div>

      <input
        type="file"
        accept="image/*"
        multiple
        disabled={uploading || disabled}
        className="input-field cursor-pointer file:mr-4 file:rounded-lg file:border-0 file:bg-ink-950 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white"
        onChange={(event) => {
          const files = Array.from(event.target.files || []);

          if (files.length) {
            onAddFiles(files);
          }

          event.target.value = '';
        }}
      />

      {uploading ? (
        <p className="text-sm font-medium text-brand-700 loading-pulse">
          Uploading images...
        </p>
      ) : null}

      {gallery.length ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {gallery.map((image, index) => {
            const angleLabel = detectAngleFromUrl(image, index);
            const isFront = index === 0;

            return (
              <div
                key={`${image}-${index}`}
                className={`gallery-tile ${isFront ? 'gallery-tile-front' : ''}`}
              >
                <div className="gallery-image-wrap">
              <img
                src={resolveAssetUrl(image)}
                alt={angleLabel}
                loading="lazy"
                decoding="async"
              />

                  <span
                    className={`absolute left-3 top-3 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ${
                      isFront
                        ? 'bg-amber-400 text-ink-950'
                        : 'bg-white/95 text-slate-700 shadow-sm'
                    }`}
                  >
                    {isFront ? 'Front' : angleLabel}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2">
                  {!isFront ? (
                    <button
                      type="button"
                      className="btn-accent px-3 py-2 text-xs"
                      disabled={disabled || uploading}
                      onClick={() => onSetCover(index)}
                    >
                      Set as Front
                    </button>
                  ) : null}

                  {ANGLE_ORDER.filter((label) => label !== 'Front').map((label) => (
                    <button
                      key={label}
                      type="button"
                      className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-all duration-200 ease-smooth ${
                        angleLabel === label
                          ? 'bg-ink-950 text-white'
                          : 'bg-white text-slate-600 hover:bg-slate-100'
                      }`}
                      disabled={disabled || uploading}
                      onClick={() => onAssignAngle(index, label)}
                    >
                      {label}
                    </button>
                  ))}

                  <button
                    type="button"
                    className="btn-secondary px-3 py-2 text-xs"
                    disabled={disabled || uploading || index === 0}
                    onClick={() => onMove(index, -1)}
                    aria-label="Move up"
                  >
                    <ChevronUp size={16} />
                  </button>

                  <button
                    type="button"
                    className="btn-secondary px-3 py-2 text-xs"
                    disabled={disabled || uploading || index === gallery.length - 1}
                    onClick={() => onMove(index, 1)}
                    aria-label="Move down"
                  >
                    <ChevronDown size={16} />
                  </button>

                  <button
                    type="button"
                    className="btn-danger-soft px-3 py-2 text-xs"
                    disabled={disabled || uploading}
                    onClick={() => setPendingRemoveIndex(index)}
                  >
                    <Trash2 size={14} />
                    Remove
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
          No angles uploaded yet. Add Front, Back, Left, Right, Bottom, or Detail photos.
        </div>
      )}

      <ConfirmModal
        open={pendingRemoveIndex !== null}
        title="Remove this angle?"
        message={
          pendingImage
            ? 'This image will be removed from the gallery when you save. Optional Cloudinary cleanup runs if enabled.'
            : 'Remove this image from the gallery?'
        }
        confirmLabel="Remove photo"
        destructive
        onCancel={() => setPendingRemoveIndex(null)}
        onConfirm={() => {
          if (pendingRemoveIndex !== null) {
            onRemove(pendingRemoveIndex);
          }

          setPendingRemoveIndex(null);
        }}
      />
    </div>
  );
}
